import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RegistrationForm from './components/RegistrationForm';
import SocialRegistration from './components/SocialRegistration';
import RegistrationSuccess from './components/RegistrationSuccess';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';


const RegisterPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form' | 'success'
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Mock user data for demonstration
  const mockUsers = [
    { email: "admin@planistry.com", password: "Admin123!" },
    { email: "user@example.com", password: "User123!" },
    { email: "demo@test.com", password: "Demo123!" }
  ];

  const handleRegister = async (formData) => {
    setLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check if email already exists (mock validation)
      const existingUser = mockUsers?.find(user => user?.email === formData?.email);
      if (existingUser) {
        throw new Error('An account with this email already exists. Please use a different email or sign in.');
      }

      // Mock successful registration
      const newUser = {
        id: Date.now(),
        email: formData?.email,
        firstName: formData?.firstName || '',
        lastName: formData?.lastName || '',
        isVerified: false,
        createdAt: new Date()?.toISOString(),
        preferences: {
          newsletter: formData?.subscribeNewsletter
        }
      };

      // Store registration data
      setRegisteredEmail(formData?.email);
      setRegistrationStep('success');

    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    setLoading(true);
    
    try {
      // Simulate social registration
      await new Promise(resolve => setTimeout(resolve, 1500));

      const mockSocialUser = {
        id: Date.now(),
        email: `user@${provider}.com`,
        firstName: 'Social',
        lastName: 'User',
        isVerified: true,
        provider: provider,
        createdAt: new Date()?.toISOString()
      };

      // Navigate directly to dashboard for social registration
      navigate('/user-account-dashboard');
      
    } catch (error) {
      console.error(`${provider} registration failed:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    
    try {
      // Simulate resend verification email
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Verification email resent to:', registeredEmail);
    } catch (error) {
      console.error('Failed to resend verification:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSkipVerification = () => {
    navigate('/user-account-dashboard');
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Same as Login */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("/assets/images/c22a2fdf-5d74-4a81-9934-01f417fff487_121132386-1766899692964.png")'
        }}
      >
        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />
      </div>
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Main Registration Card with Glassmorphism Effect */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 animate-fade-in">
            {registrationStep === 'form' ? (
              <>
                {/* Header Section */}
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-100 h-30 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30 shadow-lg p-4">
                      <AppImage 
                        src="/assets/images/stillflow_primary_white-1767144960179.svg" 
                        alt="StillFlow Logo"
                        className="w-full h-full object-contain drop-shadow-lg"
                      />
                    </div>
                  </div>
                  <h1 className="font-heading text-3xl font-bold text-white mb-2 drop-shadow-lg">
                    Create Your Account
                  </h1>
                  <p className="font-body text-white/90 text-sm drop-shadow">
                    Join StillFlow and start creating amazing planners
                  </p>
                </div>

                {/* Registration Form Section */}
                <div className="mt-8">
                  <RegistrationForm 
                    onRegister={(formData) => {
                      setRegisteredEmail(formData?.email);
                      setRegistrationStep('success');
                    }}
                    loading={loading}
                  />
                </div>

                {/* Social Registration Section */}
                <div className="mt-6">
                  <SocialRegistration 
                    onSocialRegister={async (provider) => {
                      setLoading(true);
                      try {
                        await new Promise(resolve => setTimeout(resolve, 1500));
                        navigate('/user-account-dashboard');
                      } catch (error) {
                        console.error(`${provider} registration failed:`, error);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    loading={loading}
                  />
                </div>

                {/* Sign In Link */}
                <div className="mt-6">
                  <div className="text-center">
                    <p className="font-body text-sm text-white/90 mb-3 drop-shadow">
                      Already have an account?
                    </p>
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full py-3 px-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-lg font-body text-sm font-semibold text-white transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Icon name="LogIn" size={18} />
                      <span>Sign In to Your Account</span>
                    </button>
                  </div>
                </div>

                {/* Footer Links */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
                    <button
                      onClick={() => navigate('/')}
                      className="font-body text-sm text-white/90 hover:text-white transition-colors flex items-center space-x-1 drop-shadow"
                    >
                      <Icon name="ArrowLeft" size={14} />
                      <span>Back to Home</span>
                    </button>
                    
                    <div className="flex items-center space-x-4 text-xs text-white/80">
                      <button className="hover:text-white transition-colors drop-shadow">
                        Privacy Policy
                      </button>
                      <span>•</span>
                      <button className="hover:text-white transition-colors drop-shadow">
                        Terms of Service
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <RegistrationSuccess
                email={registeredEmail}
                onResendVerification={handleResendVerification}
                onSkipVerification={handleSkipVerification}
                resendLoading={resendLoading}
              />
            )}
          </div>

          {/* Additional Help */}
          <div className="mt-6 text-center">
            <p className="font-caption text-xs text-white/90 drop-shadow">
              Need help? Contact our support team at{' '}
              <a 
                href="mailto:support@stillflow.ink" 
                className="text-white font-semibold hover:text-white/80 transition-colors"
              >
                support@stillflow.ink
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg p-6 shadow-2xl animate-scale-in">
            <div className="flex items-center space-x-3">
              <div className="animate-spin">
                <Icon name="Loader2" size={20} className="text-white" />
              </div>
              <span className="font-body text-sm text-white">
                Creating your account...
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;